import { useMemo } from "react";
import { cloneDeep } from "lodash";
import type { SearchDataModel } from "@/entities/data-model";
import { FOCUS_NAME_TO_LOCATIONS, FormLevelFeatureType, type FormMetadataResponse } from "@/entities/form-metadata";
import { useUserPreferencesQuery } from "@/entities/user-preferences";

interface UseUserPreferencesParams {
    dataModel: SearchDataModel;
    formMetadata: FormMetadataResponse;
    isEditForm?: boolean;
}

export const useUserPreferences = ({
    dataModel,
    formMetadata,
    isEditForm = false,
}: UseUserPreferencesParams): SearchDataModel => {
    const { data: userPreference } = useUserPreferencesQuery();

    return useMemo(() => {
        if (isEditForm || !userPreference) return dataModel;

        const dataModelToBeUpd = cloneDeep(dataModel);
        const collectionFocusFeature = formMetadata.features.find(
            (feature) => feature.featureType === FormLevelFeatureType.CollectionFocus,
        );
        const categoryBucketFeature = formMetadata.features.find(
            (feature) => feature.featureType === FormLevelFeatureType.CategoryBuckets,
        );
        const showUnviewedRecordsOnlyFeature = formMetadata.features.find(
            (feature) => feature.featureType === FormLevelFeatureType.ShowUnviewedRecordsOnly,
        );

        if (userPreference.givenNameExactness) {
            dataModelToBeUpd.name.givenNameExactness = userPreference.givenNameExactness;
        }
        if (userPreference.surnameExactness) {
            dataModelToBeUpd.name.surnameExactness = userPreference.surnameExactness;
        }

        // Collection Focus: URL wins > valid UPS > valid configured default > default.
        if (dataModel.priority === "default" && collectionFocusFeature) {
            const validValues = collectionFocusFeature.attributes.limitedValues.map(
                (option: { value: string }) => option.value,
            );
            const isValid = (value: string) => value === "default" || validValues.includes(value);
            const chosen =
                (userPreference.collectionFocus && isValid(userPreference.collectionFocus)
                    ? userPreference.collectionFocus
                    : null) ??
                (isValid(userPreference.defaultCollectionFocus) ? userPreference.defaultCollectionFocus : null);

            if (chosen && chosen !== "default") {
                dataModelToBeUpd.priority = chosen;
                dataModelToBeUpd.location = FOCUS_NAME_TO_LOCATIONS[chosen] ?? [];
            }
        }

        if (categoryBucketFeature) {
            dataModelToBeUpd.types = userPreference.types;
        }

        if (userPreference.showUnviewedRecordsOnly && showUnviewedRecordsOnlyFeature) {
            dataModelToBeUpd.showUnviewedRecordsOnly = userPreference.showUnviewedRecordsOnly;
        }

        return dataModelToBeUpd;
    }, [dataModel, formMetadata, isEditForm, userPreference]);
};
